import React, { useRef, useState } from "react";

import talentLenzDemonstrationArrowIcon from "../../images/talentlenz-video-hero-demonstration-arrow-icon.svg";
import talentLenzHeroBackground from "../../images/talentlenz-video-hero-luminous-background.svg";
import talentLenzOpportunityPoster from "../../images/talentlenz-video-hero-opportunity-match-poster.svg";
import talentLenzPlayIcon from "../../images/talentlenz-video-hero-play-icon.svg";
import talentLenzProductMark from "../../images/talentlenz-video-hero-product-mark.svg";

import "./TalentLenzVideoHero.css";

const TALENTLENZ_OPPORTUNITY_YOUTUBE_ID = "lLFrIVZpbNM";
const TALENTLENZ_OPPORTUNITY_YOUTUBE_EMBED_URL = `https://www.youtube.com/embed/${TALENTLENZ_OPPORTUNITY_YOUTUBE_ID}?autoplay=1&rel=0&modestbranding=1`;

export default function TalentLenzVideoHero({
  demonstrationHref = "#talentlenz-demonstration",
}) {
  const talentLenzVideoCardRef = useRef(null);
  const [videoHasStarted, setVideoHasStarted] = useState(false);

  const handleTalentLenzVideoPlay = () => {
    setVideoHasStarted(true);
  };

  const handleTalentLenzPrimaryAction = () => {
    handleTalentLenzVideoPlay();

    if (
      typeof window === "undefined" ||
      !window.matchMedia("(max-width: 900px)").matches
    ) {
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    window.requestAnimationFrame(() => {
      talentLenzVideoCardRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "center",
      });
    });
  };

  return (
    <section
      id="talentlenz-video-hero"
      className="talentlenz-video-hero"
      aria-labelledby="talentlenz-video-hero-title"
    >
      <img
        className="talentlenz-video-hero__background"
        src={talentLenzHeroBackground}
        alt=""
        aria-hidden="true"
        draggable="false"
      />

      <div className="talentlenz-video-hero__inner">
        <div className="talentlenz-video-hero__copy">
          <div className="talentlenz-video-hero__brand">
            <img
              className="talentlenz-video-hero__brand-mark"
              src={talentLenzProductMark}
              alt=""
              aria-hidden="true"
              draggable="false"
            />
            <span className="talentlenz-video-hero__brand-label">
              TalentLenz
            </span>
          </div>

          <h1
            id="talentlenz-video-hero-title"
            className="talentlenz-video-hero__title"
          >
            Opportunity intelligence that finds fit before the interview.
          </h1>

          <p className="talentlenz-video-hero__description">
            TalentLenz turns applications into evidence-based opportunity
            matching so teams can select with confidence, not noise.
          </p>

          <div className="talentlenz-video-hero__actions">
            <button
              type="button"
              className="talentlenz-video-hero__action talentlenz-video-hero__action--primary"
              onClick={handleTalentLenzPrimaryAction}
            >
              <span>Watch product film</span>

              <img
                className="talentlenz-video-hero__action-arrow"
                src={talentLenzDemonstrationArrowIcon}
                alt=""
                aria-hidden="true"
                draggable="false"
              />
            </button>

            <a
              className="talentlenz-video-hero__action talentlenz-video-hero__action--secondary"
              href={demonstrationHref}
            >
              Book a demonstration
            </a>
          </div>
        </div>

        <div className="talentlenz-video-hero__media">
          <div
            ref={talentLenzVideoCardRef}
            className={`talentlenz-video-hero__video-card${
              videoHasStarted
                ? " talentlenz-video-hero__video-card--started"
                : ""
            }`}
          >
            <div className="talentlenz-video-hero__video-stage">
              {videoHasStarted ? (
                <iframe
                  id="talentlenz-opportunity-product-film"
                  className="talentlenz-video-hero__video talentlenz-video-hero__video--youtube"
                  src={TALENTLENZ_OPPORTUNITY_YOUTUBE_EMBED_URL}
                  title="TalentLenz Opportunity product film"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <>
                  <img
                    className="talentlenz-video-hero__video-poster"
                    src={talentLenzOpportunityPoster}
                    alt=""
                    aria-hidden="true"
                    draggable="false"
                  />

                  <button
                    type="button"
                    className="talentlenz-video-hero__video-overlay"
                    onClick={handleTalentLenzVideoPlay}
                    aria-label="Play the TalentLenz opportunity intelligence product film"
                  >
                    <img
                      className="talentlenz-video-hero__play-icon"
                      src={talentLenzPlayIcon}
                      alt=""
                      aria-hidden="true"
                      draggable="false"
                    />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="talentlenz-video-hero__video-caption">
            <p className="talentlenz-video-hero__video-caption-title">
              From application overload to
              <br className="talentlenz-video-hero__caption-mobile-break" />
              {" "}evidence-based selection.
            </p>

            <p className="talentlenz-video-hero__video-caption-meta">
              <span>YouTube product film</span>
              <span aria-hidden="true"> · </span>
              <span>Opportunity intelligence</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
